import { type FormEvent, useState } from "react";
import { Card } from "../../components/ui/Card";
import { useAuthStore } from "../../lib/auth/authStore";
import { useEmailLogin } from "../../lib/auth/useEmailLogin";

/**
 * Full-screen login gate. An email + password form that exchanges the
 * credentials for a session token. Surfaces a single coarse error (the
 * backend never reveals *why* a login failed).
 */
export function LoginScreen() {
	const { runLogin } = useEmailLogin();

	const status = useAuthStore((s) => s.status);
	const error = useAuthStore((s) => s.error);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const submitting = status === "submitting";

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (submitting) return;
		void runLogin(email, password);
	};

	return (
		<div className="flex min-h-[70vh] items-center justify-center">
			<Card className="w-full max-w-md">
				<h1 className="text-center text-lg font-semibold text-slate-100">
					KAM Yield
				</h1>
				<p className="mt-2 text-center text-sm text-slate-400">
					Sign in with your email and password to access the dashboard.
				</p>

				<form onSubmit={onSubmit} className="mt-6 space-y-4">
					<div className="space-y-1.5 text-left">
						<label
							htmlFor="email"
							className="block text-xs font-medium text-slate-400"
						>
							Email
						</label>
						<input
							id="email"
							type="email"
							autoComplete="username"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={submitting}
							className="w-full rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none transition focus:border-emerald-600 disabled:opacity-60"
							placeholder="you@example.com"
						/>
					</div>

					<div className="space-y-1.5 text-left">
						<label
							htmlFor="password"
							className="block text-xs font-medium text-slate-400"
						>
							Password
						</label>
						<input
							id="password"
							type="password"
							autoComplete="current-password"
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							disabled={submitting}
							className="w-full rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none transition focus:border-emerald-600 disabled:opacity-60"
							placeholder="••••••••"
						/>
					</div>

					<button
						type="submit"
						disabled={submitting}
						className="w-full rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{submitting ? "Signing in…" : "Sign in"}
					</button>
				</form>

				{error && (
					<p className="mt-4 rounded-md border border-rose-900/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-300">
						{error}
					</p>
				)}
			</Card>
		</div>
	);
}
